import type { TanyaIntent } from "@/lib/tanya/types";
import { synthesiseIntent } from "@/lib/tanya/defaults";

/**
 * A call-to-action must never be able to do nothing.
 *
 * Historically these helpers fired a `tanya:open` window event and stopped
 * there. The only listener lives inside ChatWidget, and campaign pages set to
 * "Form only" capture never mount it — so every button on such a page was a
 * dead click: no navigation, no form, no error.
 *
 * The delivery order below guarantees a destination:
 *   1. Tanya is already mounted  → open her (unchanged behaviour).
 *   2. The page carries a lead form → scroll to it and focus the first field.
 *   3. Otherwise → ask the page shell to mount Tanya, then replay the event.
 *   4. If she still has not appeared after a few seconds → go to /contact.
 */
function tanyaIsMounted() {
  return typeof window !== "undefined" && (window as any).__tanyaMounted === true;
}

function deliver(detail: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const fire = () => window.dispatchEvent(new CustomEvent("tanya:open", { detail }));

  if (tanyaIsMounted()) {
    fire();
    return;
  }

  const form = document.querySelector<HTMLElement>("[data-lead-capture]");
  if (form) {
    form.scrollIntoView({ behavior: "smooth", block: "center" });
    const field = form.querySelector<HTMLElement>("input, select, textarea");
    window.setTimeout(() => field?.focus({ preventScroll: true }), 400);
    return;
  }

  window.dispatchEvent(new CustomEvent("tanya:mount-request"));
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (tanyaIsMounted()) {
      window.clearInterval(timer);
      fire();
    } else if (tries > 60) {
      window.clearInterval(timer);
      window.location.href = "/contact";
    }
  }, 100);
}

/**
 * Dispatch a request to open Tanya.
 * Back-compat: callers may still pass (mode, context). ChatWidget will
 * synthesise a TanyaIntent from the current page URL + context when no
 * intent is supplied.
 */
export function openTanya(
  mode?: string,
  context?: string,
  extra?: { assetId?: string },
) {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const intent = synthesiseIntent(path, { mode, context });
  deliver({ mode, context, intent, assetId: extra?.assetId });
}

/**
 * Preferred entry point — pass a structured intent so Tanya can open
 * with a tailored greeting and predictive next-step chips.
 */
export function openTanyaWithIntent(intent: TanyaIntent) {
  deliver({ mode: intent.mode, context: intent.topic?.title, intent });
}

export function useTanyaTrigger() {
  return { openTanya, openTanyaWithIntent };
}
